import { of } from 'rxjs'
import { ajax } from 'rxjs/ajax'
import { catchError, map, retry } from 'rxjs/operators'
import envGet from '../../Support/Env/Get/EnvGet.support'
import { buildUrl } from 'Support/Url'
import { NotificationConfig } from 'types/NotificationConfig';
import getNotificationsService, { getNotificationsServiceObservable } from './services/get-notifications'

const baseUrl = envGet({ key: `REACT_APP_BORAL_BASE_URL` })! as string
const BASE_URL_FLITCH = envGet({ key: `REACT_APP_BORAL_BASE_URL_FLITCH` })! as string

const NOTIFICATIONS_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATIONS_URL` })! as string
const NOTIFICATIONS_MARK_READ_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATIONS_MARK_READ_URL` })! as string
const notificationIdMarkReadUrl = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_ID_MARK_READ_URL` })! as string
const notificationsRecordActionUrl = envGet({ key: `REACT_APP_BORAL_NOTIFICATIONS_RECORD_ACTION_URL` })! as string
const notificationsHasNotificationsUrl = envGet({ key: `REACT_APP_BORAL_NOTIFICATIONS_HAS_UNREAD_URL` })! as string


const NOTIFICATION_ACCOUNT_SETTINGS_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_ACCOUNT_SETTINGS_URL` })! as string
const NOTIFICATION_ACCOUNT_SETTINGS_PUT_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_ACCOUNT_SETTINGS_PUT_URL` })! as string
const NOTIFICATION_PROJECT_SETTINGS_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_PROJECT_SETTINGS_URL` })! as string
const NOTIFICATION_PROJECT_SETTINGS_PUT_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_PROJECT_SETTINGS_PUT_URL` })! as string
const NOTIFICATION_SMS_ENABLING_CHECKED_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_SMS_ENABLING_CHECKED_URL` })! as string
const NOTIFICATION_MARK_SMS_ENABLING_CHECKED_URL = envGet({ key: `REACT_APP_BORAL_NOTIFICATION_MARK_SMS_ENABLING_CHECKED_URL` })! as string


class NotificationService {
  authService: any

  constructor({ authService, httpService }: any) {
    this.authService = authService;
  }

  getNotifications = (howMany: number, type?: string) => {
    return getNotificationsService({
      authHeaders: this.authService.createHeaders(),
      // httpService: this.httpService,
      howMany,
      type,
    })
  }

  getNotificationsObservable = (howMany: number, type?: string) => {
    return getNotificationsServiceObservable({
      authHeaders: this.authService.createHeaders(),
      // httpService: this.httpService,
      howMany,
      type,
    })
  }


  recordAction = (action: any) => {
    const headers = this.authService.createHeaders();
    return ajax({
      body: {
        action,
      },
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `PUT`,
      url: `${baseUrl}${notificationsRecordActionUrl}`,
    })
      .pipe(catchError(() => of(`Error occured`)))
      .toPromise();
  };

  /**
   * markAllRead
   * @returns {promise} Promise that resolves to last emitted value
   */
  markAllRead = () => {
    const headers = this.authService.createHeaders();
    return (
      ajax({
        headers,
        method: `PUT`,
        url: `${BASE_URL_FLITCH}${NOTIFICATIONS_MARK_READ_URL}`,
      })
        .pipe(
          // for now non existent API obviously fail so we fake response. replace this with proper error handling when api is ready
          catchError(() => of(`Error occured`)),
        )
        // above we can still use RXJS operators but at the end we want to allow async/awain in wherever markAllRead is called
        .toPromise()
    );
  };

  /**
   * mark single notification as read
   * @param notificationId how many notifications to retrieve
   * @returns {promise} Promise that resolves to last emitted value
   */
  markIdRead = (notificationId: string) => {
    const headers = this.authService.createHeaders();
    return (
      ajax({
        headers,
        method: `PUT`,
        url: `${baseUrl}${notificationIdMarkReadUrl.replace(`NOTIFICATION_ID`, notificationId)}`,
      })
        .pipe(
          // for now non existent API obviously fail so we fake response. replace this with proper error handling when api is ready
          catchError(() => of(`Error occured`)),
        )
        // above we can still use RXJS operators but at the end we want to allow async/awain in wherever markAllRead is called
        .toPromise()
    );
  };

  /**
   * hasNotifications
   * @returns {promise} Promise that resolves to last emitted value
   */
  hasNotifications = () => {
    const headers = this.authService.createHeaders();
    return (
      ajax({
        headers,
        method: `GET`,
        url: `${baseUrl}${notificationsHasNotificationsUrl}`,
      })
        .pipe(
          // for now non existent API obviously fail so we fake response. replace this with proper error handling when api is ready
          catchError(() => of(`Error occured`)),
        )
        // above we can still use RXJS operators but at the end we want to allow async/awain in wherever markAllRead is called
        .toPromise()
    );
  };

  getAccountConfig = (accountId: string) => {
    const headers = this.authService.createHeaders()
    const url = buildAccountConfigUrl(accountId)
    return ajax({
      headers,
      url,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || []),
      )
      .toPromise()
  }

  getProjectConfig = (projectId: string) => {
    const headers = this.authService.createHeaders()
    const url = buildProjectConfigUrl(projectId)
    return ajax({
      headers,
      url,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || []),
      )
      .toPromise()
  }

  saveConfigs = async ({
    belongsToId,
    accountId,
    configs,
    isProjectConfig,
  }: {
    belongsToId: string,
    accountId: string,
    configs: NotificationConfig[],
    isProjectConfig: boolean,
  }) => {
    const headers = this.authService.createHeaders()
    const body = {
      accountId,
      configs,
    }
    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `PUT`,
      url: isProjectConfig
        ? buildProjectConfigUrl(belongsToId, true)
        : buildAccountConfigUrl(belongsToId, true),
    })
      .pipe(retry(1))
      .toPromise()
  };

  getSMSEnablingChecked = () => {
    const headers = this.authService.createHeaders()
    const url = `${BASE_URL_FLITCH}${NOTIFICATION_SMS_ENABLING_CHECKED_URL}`
    return ajax({
      headers,
      url,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || { isBannerChecked: false }),
      )
      .toPromise()
  }

  markSMSEnablingChecked = () => {
    const headers = this.authService.createHeaders()
    const url = `${BASE_URL_FLITCH}${NOTIFICATION_MARK_SMS_ENABLING_CHECKED_URL}`
    return ajax({
      headers,
      url,
      method: `PUT`,
    })
      .pipe(
        map((ajaxObject) => ajaxObject.response || []),
      )
      .toPromise()
  }
}

function buildAccountConfigUrl(accountId: string, isSaving?: boolean) {
  return `${BASE_URL_FLITCH}${(!isSaving ? NOTIFICATION_ACCOUNT_SETTINGS_URL : NOTIFICATION_ACCOUNT_SETTINGS_PUT_URL).replace(`{ACCOUNT}`, accountId.trim())}`
}
function buildProjectConfigUrl(projectId: string, isSaving?: boolean) {
  return `${BASE_URL_FLITCH}${(!isSaving ? NOTIFICATION_PROJECT_SETTINGS_URL : NOTIFICATION_PROJECT_SETTINGS_PUT_URL).replace(`{PROJECT}`, projectId.trim())}`
}
export default NotificationService;
